import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { AuthStatus } from 'constants/auth';
import { COACH_HOME_PAGE, HOME_PAGE, ROOT_PAGE } from 'constants/pages';
import { CoachStatusEnum, SportsEnum } from 'types/generated/client';
import { useAuthModals } from 'hooks/useAuthModals';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import Logo from 'svg/LogoNav';
import { ExploreAllMarketPlacesTypesEnum } from 'components/DiscoverPage/props';
import EventTypeModal from 'components/HostedEvents/EventTypeModal';
import Link from 'components/Link';
import SearchBar from 'components/SearchBar';
import TransitionFadeIn from 'components/TransitionFadeIn';
import ModalActivateCoachAccount from 'components/modals/ModalActivateCoachAccount';
import ModalNotification from 'components/modals/ModalNotification';
import classNames from 'styles/utils/classNames';
import AnonAuthButtons, { AnonAuthPills } from './AnonAuthButtons';
import CoachNavLinks from './CoachNavLinks';
import PlayerNavLinks from './PlayerNavLinks';
import TopNavSkeleton from './TopNavSkeleton';
import UserActions from './UserActions';
import { getHasCoachOnboarded } from './utils';

export interface TopNavSearchConfig {
  /** Renders a search bar in the nav center, replacing nav links. */
  isEnabled: boolean;
  /**
   * Pixels scrolled from the top before the nav search bar becomes visible.
   * Useful when the page already has a hero search bar — keeps it hidden
   * until the user scrolls past it. When undefined, the search bar is always
   * visible.
   */
  scrollOffset?: number;
  /**
   * When true, the mobile second row (the search bar) floats over page
   * content instead of pushing it down: the nav container collapses back to
   * `h-topnav`, the spacer only reserves the primary row, and the search
   * bar has no background of its own. Used on marketplace pages where the
   * map/content should fill the viewport under the nav. Desktop is
   * unaffected since the search bar lives inline inside the primary row
   * there.
   */
  isSecondRowOverlay?: boolean;
  /**
   * When true, the mobile second row (search bar) renders in normal document
   * flow instead of being pinned inside the fixed nav — it scrolls away with
   * the page content. Used on marketplace list views where the search bar
   * should disappear as the user scrolls the list. Desktop is unaffected
   * since the search bar is inline in the primary row.
   *
   * Mutually exclusive with `isSecondRowOverlay` — overlay pins the bar over
   * content (map view), scrollable lets it flow with content (list view).
   */
  isSecondRowScrollable?: boolean;
  /**
   * Pre-populate the explore type dropdown in the search bar.
   * Defaults to `Lessons` when omitted.
   */
  defaultExploreType?: ExploreAllMarketPlacesTypesEnum;
  /**
   * Pre-populate the sport dropdown in the search bar.
   * Defaults to `Pickleball` when omitted.
   */
  defaultSport?: SportsEnum;
  /**
   * Called when the user selects an address in the search bar.
   * Receives the geocoded coordinates of the selected address.
   * Used by static city pages to navigate to the generic marketplace
   * with the new location instead of staying on the static URL.
   */
  onAddressChange?: (coords: { latitude: number; longitude: number }) => void;
  /**
   * Hides the search button and fires navigation immediately on any
   * dropdown or address change. Useful for pages that already have
   * a button or want live-filter behaviour.
   */
  instant?: boolean;
}

/**
 * 'default'          — primary row only, no search bar in the nav
 * 'searchVisible'    — search bar has taken over the nav
 *                      (center on desktop, second row on mobile)
 * 'searchPreScroll'  — search is enabled but hidden (waiting to scroll past
 *                      the hero search); on mobile, anon users see a second
 *                      row with the coach/club pills
 */
type NavMode = 'default' | 'searchVisible' | 'searchPreScroll';

interface Props {
  shouldShowAdditionalLinks?: boolean;
  shouldHideNavigation: boolean;
  shouldShowMobile?: boolean;
  shouldLinkToAuthPage?: boolean;
  shouldShowStartAction?: boolean;
  isBlur?: boolean;
  isInvert?: boolean;
  isTransparent?: boolean;
  handleSignupSuccess?: ({ userId }: { userId: string }) => void | Promise<void>;
  topNavSearch?: TopNavSearchConfig;
}

export default function TopNav({
  shouldHideNavigation,
  shouldLinkToAuthPage = false,
  shouldShowStartAction,
  isBlur,
  isInvert,
  isTransparent,
  handleSignupSuccess,
  topNavSearch,
}: Props) {
  const isSecondRowOverlay = !!topNavSearch?.isSecondRowOverlay;
  const isSecondRowScrollable = !!topNavSearch?.isSecondRowScrollable;
  const router = useRouter();
  const viewer = useViewer();
  const { user, loading, called } = useGetCurrentUser();
  const { ModalLogin, ModalSignup, openSignupModal, openLoginModal } = useAuthModals();

  const {
    isOpen: isEventTypeModalOpen,
    openModal: openEventTypeModal,
    closeModal: closeEventTypeModal,
  } = useModal();
  const {
    isOpen: isModalNotificationOpen,
    openModal: openModalNotification,
    closeModal: closeModalNotification,
  } = useModal();
  const {
    isOpen: isModalActivateCoachOpen,
    openModal: openModalActivateCoach,
    closeModal: closeModalActivateCoach,
  } = useModal();

  // --- Auth state ---------------------------------------------------------
  const isViewerLoaded = viewer.status !== AuthStatus.Loading;
  const isAnon = viewer.status === AuthStatus.Anonymous;
  const isUser = viewer.status === AuthStatus.User;
  const isCoach = user?.coachStatus === CoachStatusEnum.Active;
  const isUserLoaded = isViewerLoaded && !loading && called;
  const isAnonReady = isAnon && isViewerLoaded;
  const isUserReady = isUser && isUserLoaded;
  const isShowing = isAnonReady || isUserReady;
  const isLoading = !isViewerLoaded || (isUser && (!called || loading));

  // --- Search / nav mode --------------------------------------------------
  const isSearchEnabled = !!topNavSearch?.isEnabled;
  const scrollOffsetRef = useRef(topNavSearch?.scrollOffset);
  scrollOffsetRef.current = topNavSearch?.scrollOffset;

  const deriveMode = useCallback((): NavMode => {
    if (!isSearchEnabled) return 'default';
    if (typeof window === 'undefined') return 'default';
    const offset = scrollOffsetRef.current;
    const hasScrolledPast = offset === undefined || window.scrollY >= offset;
    return hasScrolledPast ? 'searchVisible' : 'searchPreScroll';
  }, [isSearchEnabled]);

  const [navMode, setNavMode] = useState<NavMode>(() =>
    isSearchEnabled && typeof window !== 'undefined' ? deriveMode() : 'default',
  );

  useEffect(() => {
    if (!isSearchEnabled) return;
    const handleScroll = () => {
      const next = deriveMode();
      setNavMode((prev) => (prev === next ? prev : next));
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isSearchEnabled, deriveMode]);

  const isSearchVisible = navMode === 'searchVisible';
  const isSearchPreScroll = navMode === 'searchPreScroll';

  // --- Navigation interception (coach onboarding gate) -------------------
  const handleNavigate = (href: string) => {
    const isHomePage = href === COACH_HOME_PAGE || href === HOME_PAGE || href === ROOT_PAGE;
    const isOnboardingComplete = isCoach && getHasCoachOnboarded(user);

    if (isCoach && !isOnboardingComplete && !isHomePage) {
      openModalActivateCoach();
      return;
    }

    router.push(href);
  };

  // --- Layout classes -----------------------------------------------------
  // Height model:
  //   - Primary row is always `h-topnav` so pages can anchor sticky content
  //     at `top-topnav`.
  //   - When the search bar is visible, the nav grows to fit it:
  //       mobile <480:  160px (row1 64 + row2 96 for stacked search bar)
  //       mobile 480+:  120px (row1 64 + row2 56 for shorter search bar)
  //       desktop 768+: 88px  (search bar inline inside row1)
  //   - The SPACER is 10px taller than the nav whenever the search bar is
  //     visible, leaving breathing room below the bar before page content
  //     starts (170 / 130 / 98 respectively).
  //   - The anon "I'm a coach / I'm a club" second row (no search) uses
  //     104px with no breathing offset.
  //   - OVERLAY MODE (`isSecondRowOverlay` + search visible): on mobile, the
  //     nav container collapses back to `h-topnav` and its background only
  //     covers the primary row. The absolutely-positioned mobile second row
  //     escapes the container box and floats over page content with no
  //     background of its own. Desktop keeps its normal search-inline
  //     layout since the search bar lives inside the primary row there.
  const isMobileSearchOverlay = isSecondRowOverlay && isSearchVisible;
  const isMobileSearchScrollable = isSecondRowScrollable && isSearchVisible;

  const navContainerClass = classNames(
    'fixed left-0 top-0 z-40 w-full transition-[height] duration-300 ease-in-out',
    isMobileSearchOverlay || isMobileSearchScrollable
      ? 'h-topnav-mobile @[1024px]:h-topnav-search'
      : isSearchVisible
      ? 'h-[156px] @[480px]:h-[116px] @[1024px]:h-topnav-search'
      : isSearchPreScroll && isAnon
      ? 'h-[100px] @[768px]:h-topnav'
      : 'h-topnav-mobile @[768px]:h-topnav',
    isBlur && 'bg-opacity-80 backdrop-blur-md',
    isTransparent
      ? 'bg-transparent'
      : isInvert
      ? 'bg-color-bg-lightmode-invert dark:bg-color-bg-darkmode-invert'
      : classNames(
          'bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary',
          !isSearchVisible &&
            !isAnon &&
            'border-b border-color-border-input-lightmode dark:border-color-border-input-darkmode',
        ),
  );

  const spacerClass = classNames(
    'transition-[height] duration-300 ease-in-out',
    isMobileSearchOverlay || isMobileSearchScrollable
      ? 'h-topnav-mobile @[1024px]:h-topnav-search'
      : isSearchVisible
      ? // nav heights + 10px breathing room below the search bar
        'h-[166px] @[480px]:h-[126px] @[1024px]:h-topnav-search'
      : isSearchPreScroll && isAnon
      ? 'h-[100px] @[768px]:h-topnav'
      : 'h-topnav-mobile @[768px]:h-topnav',
    isBlur && !isInvert && 'bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary',
    isBlur && isInvert && 'bg-color-bg-lightmode-invert dark:bg-color-bg-darkmode-invert',
  );

  return (
    <>
      <div className="block w-full @container">
        <div className={navContainerClass}>
          {/* Primary row ------------------------------------------------ */}
          {/* Grows with the container, or its contents sit top-aligned in the taller
              tablet bar. With the search bar up the container also holds the second
              row below, so there the primary row keeps its own height until lg. */}
          <div
            className={classNames(
              'flex h-topnav-mobile w-full items-center justify-between',
              isSearchVisible ? '@[1024px]:h-full' : '@[768px]:h-full',
            )}
          >
            {/* Left: logo */}
            <div className="relative z-10 flex h-full items-center pl-4 sm:pl-8">
              <Link
                href={isUser ? HOME_PAGE : ROOT_PAGE}
                className="inline-block"
                aria-label="Home"
              >
                <div className="flex h-full items-center">
                  <Logo className="h-6" />
                </div>
              </Link>
            </div>

            {/* Loading skeleton */}
            {isLoading && <TopNavSkeleton shouldHideNavigation={shouldHideNavigation} />}

            {/* Center: nav links or desktop search bar */}
            {!isLoading && (
              <div className="absolute left-0 right-0 mx-auto flex h-topnav-mobile w-full items-center justify-center @[768px]:h-full">
                <div className="relative my-3 flex h-full w-full items-center justify-center">
                  <TransitionFadeIn
                    isShowing={isSearchVisible}
                    className="absolute inset-0 hidden items-center justify-center @[1024px]:flex"
                  >
                    <SearchBar
                      isInNav
                      defaultExploreType={topNavSearch?.defaultExploreType}
                      defaultSport={topNavSearch?.defaultSport}
                      onAddressChange={topNavSearch?.onAddressChange}
                      instant={topNavSearch?.instant}
                    />
                  </TransitionFadeIn>
                  <TransitionFadeIn
                    isShowing={!isSearchVisible}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div
                      className={classNames(
                        'h-12 flex-row gap-3 rounded-full border border-color-border-input-lightmode shadow-sm dark:border-color-border-input-darkmode xs:hidden md:flex',
                        isAnon && 'border-transparent shadow-none',
                      )}
                    >
                      {!shouldHideNavigation && isShowing && !isAnon && !isCoach && (
                        <PlayerNavLinks
                          currentPath={router.asPath}
                          currentPathname={router.pathname}
                          isInvert={isInvert}
                          onNavigate={handleNavigate}
                          onOpenEventTypeModal={openEventTypeModal}
                        />
                      )}
                      {!shouldHideNavigation && isShowing && !isAnon && isCoach && (
                        <CoachNavLinks
                          currentPath={router.asPath}
                          currentPathname={router.pathname}
                          isInvert={isInvert}
                          onNavigate={handleNavigate}
                          onOpenEventTypeModal={openEventTypeModal}
                        />
                      )}
                    </div>
                  </TransitionFadeIn>
                </div>
              </div>
            )}

            {/* Right: auth buttons (anon) or user actions */}
            <div className="relative z-10 flex h-full flex-row items-center gap-4 pr-4 sm:pr-8">
              <TransitionFadeIn isShowing={isShowing}>
                {isAnon ? (
                  <AnonAuthButtons
                    shouldLinkToAuthPage={shouldLinkToAuthPage}
                    isInvert={isInvert}
                    isCompact={isSearchVisible}
                    shouldHidePillsOnMobile
                    onOpenLoginModal={() => openLoginModal(true)}
                    onOpenSignupModal={() => openSignupModal(true)}
                  />
                ) : (
                  <UserActions
                    isCoach={!!isCoach}
                    profileImagePath={user?.profileImagePath}
                    shouldShowStartAction={shouldShowStartAction}
                    onOpenNotifications={() => openModalNotification(true)}
                    onNavigate={handleNavigate}
                  />
                )}
              </TransitionFadeIn>
            </div>
          </div>

          {/* Mobile second row -----------------------------------------
              Absolutely positioned at `top-topnav` (just below the primary
              row) and wrapped in `TransitionFadeIn` — matches the desktop
              pattern so mount/unmount happens via opacity fade instead of
              instant DOM insertion. The container's own height animation
              runs in parallel: the container grows under the fading content
              and finishes its transition at the same time.

              When `isSecondRowScrollable` is active, this pinned version is
              hidden on mobile — the search bar renders in normal document
              flow after the spacer instead (see below). */}
          <TransitionFadeIn
            isShowing={isSearchVisible && !isMobileSearchScrollable}
            className="absolute inset-x-0 top-topnav-mobile px-4 pb-3 pt-3 @[1024px]:hidden"
          >
            <SearchBar
              isInNav
              defaultExploreType={topNavSearch?.defaultExploreType}
              defaultSport={topNavSearch?.defaultSport}
              instant={topNavSearch?.instant}
            />
          </TransitionFadeIn>
          <TransitionFadeIn
            isShowing={isSearchPreScroll && isAnon}
            className="absolute inset-x-0 top-topnav-mobile pb-3 pt-3 @[768px]:hidden"
          >
            <AnonAuthPills isInvert={isInvert} onOpenLoginModal={() => openLoginModal(true)} />
          </TransitionFadeIn>
        </div>

        {!isTransparent && <div className={spacerClass} />}

        {/* Scrollable mobile search bar -----------------------------------
            When `isSecondRowScrollable` is true, the search bar renders in
            normal document flow (outside the fixed nav) so it scrolls away
            with page content. Only visible on mobile — desktop always has
            the search bar inline in the primary row. */}
        <TransitionFadeIn
          isShowing={isMobileSearchScrollable}
          className="bg-color-bg-lightmode-primary px-4 pb-3 pt-3 @[1024px]:hidden dark:bg-color-bg-darkmode-primary"
        >
          <SearchBar
            isInNav
            defaultExploreType={topNavSearch?.defaultExploreType}
            defaultSport={topNavSearch?.defaultSport}
            instant={topNavSearch?.instant}
          />
        </TransitionFadeIn>
      </div>

      <ModalSignup handleSignupSuccess={handleSignupSuccess} isShowLoginLink />
      <ModalLogin isShowSignupLink />
      <EventTypeModal
        isOpen={isEventTypeModalOpen}
        onClose={closeEventTypeModal}
        onOpen={openEventTypeModal}
      />
      <ModalNotification
        isOpen={isModalNotificationOpen}
        closeModal={closeModalNotification}
        title="Notifications"
      />
      <ModalActivateCoachAccount
        isOpen={isModalActivateCoachOpen}
        handleClose={closeModalActivateCoach}
      />
    </>
  );
}
